import React, { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import raceApi from '../../clients/RaceClient';
import driverApi from '../../clients/DriverClient';
import { useAuth } from '../../context/AuthContext';
import { normalizeLocationKey } from '../../clients/locationApiBase';
import { resolvePreferredLocation } from '../../utils/preferredLocation';
import PageBreadcrumb from '../shared/PageBreadcrumb';
import GuestPaymentForm from '../booking/shared/GuestPaymentForm';
import { useGuestHold } from '../booking/shared/useGuestHold';
import type { Membership } from '../../models/Membership';
import type {
  ClaimRaceNightBody, ClaimRaceNightResult, RaceNight, RaceNightGuestSeat, RigClass,
} from '../../models/raceNights/RaceNight';
import type { GuestPaymentMethod } from '../../models/requestBodies/RedeemEntitlementRequestBody';
import {
  defaultRig, effectivePayment, groupOf, includedLimitMessage, WEBSITE_MEMBERSHIPS, type NightGroup,
} from '../../utils/raceNightFormat';
import RaceNightRow from './RaceNightRow';
import RaceNightPanel, { type GuestPayment, type MemberPayment } from './RaceNightPanel';
import '../booking/entitlement/EntitlementWizard.css';
import '../booking/shared/SlotRows.css';
import './RaceNights.css';

export const VENUES: { key: string; label: string }[] = [
  { key: 'pasadena', label: 'Pasadena' },
  { key: 'tustin', label: 'Tustin' },
  { key: 'kansascity', label: 'Kansas City' },
  { key: 'phoenix', label: 'Phoenix' },
];
const GROUPS: NightGroup[] = ['This week', 'Next week', 'Later'];

const PENDING_KEY = 'wor.raceNights.pendingGuestPayment';
// The server's hold window (PublicAvailabilityService.HoldWindow). A restored hold older than this is
// released rather than paid for: its seat may already be sold again (Review Focus 1).
const HOLD_MS = 15 * 60_000;
// PaymentMethod.Stripe is ZERO: never test it for truthiness.
const PAYMENT_METHOD_STRIPE: GuestPaymentMethod = 0;
// Refusals that mean "the night filled while you were looking": reload so the row offers the waitlist.
const FULL_REASONS = ['classFull', 'soldOut', 'heldForWaitlist'];

/** The member's race nights: what their tier gets on each night at a venue, and claiming a seat. */
const RaceNights: React.FC = () => {
  const { user, balance, refreshBalance } = useAuth();
  const [searchParams] = useSearchParams();
  // A link from the home carries the venue it was showing (?venue=); an unknown one falls back to the preference.
  const [location, setLocation] = useState(() => {
    const isVenue = (key: string) => VENUES.some((v) => v.key === key);
    return resolvePreferredLocation(searchParams.get('venue'), isVenue, normalizeLocationKey)
      || resolvePreferredLocation(user?.preferredLocation, isVenue, normalizeLocationKey);
  });
  const [nights, setNights] = useState<RaceNight[] | null>(null);
  const [loadError, setLoadError] = useState('');
  const [openId, setOpenId] = useState<string | null>(null);
  // undefined = still loading; null = no membership.
  const [membership, setMembership] = useState<Membership | null | undefined>(undefined);
  const [notice, setNotice] = useState('');
  const [rig, setRig] = useState<RigClass>('pro');
  // The member's own seat: null = not picked yet, so the panel preselects the first available option.
  const [payment, setPayment] = useState<MemberPayment | null>(null);
  const [guests, setGuests] = useState<RaceNightGuestSeat[]>([]);
  const [busy, setBusy] = useState(false);
  const [panelError, setPanelError] = useState<{ message: string; buyCredits?: boolean } | null>(null);
  const [booked, setBooked] = useState<{ reference: string; hasCode: boolean } | null>(null);

  const load = useCallback(async () => {
    setLoadError('');
    try {
      setNights(await raceApi.listRaceNights(location));
    } catch {
      setNights([]);
      setLoadError('Couldn’t load race nights just now. Please try again.');
    }
  }, [location]);

  useEffect(() => { setNights(null); setOpenId(null); void load(); }, [load]);

  useEffect(() => {
    const driverId = user?.driverId;
    if (!driverId) { setMembership(null); return; }
    let cancelled = false;
    // Promise.resolve().then so a client that throws synchronously cannot take the page down.
    Promise.resolve()
      .then(() => driverApi.getMembershipByDriverId(driverId))
      .then((m) => { if (!cancelled) setMembership(m ?? null); })
      .catch(() => { if (!cancelled) setMembership(null); });
    return () => { cancelled = true; };
  }, [user?.driverId]);

  // The card step for guests: the same lifecycle the pass booking page uses (P0), keyed to this page.
  const hold = useGuestHold({
    storageKey: PENDING_KEY,
    fallbackLocation: location,
    maxHoldAgeMs: HOLD_MS,
    // Every way a hold ends can move credits (a credit seat is spent at hold time and returned on
    // release), so each one refreshes the header balance (spec 4.4).
    onBooked: (reference, _bookedAt, hasCode) => {
      void refreshBalance();
      setBooked({ reference, hasCode }); setOpenId(null); setGuests([]); void load();
    },
    onReleased: () => { void refreshBalance(); void load(); },
    onExpired: () => {
      void refreshBalance();
      setNotice('Your seat hold expired, so nothing was booked. Pick the night again.'); void load();
    },
  });

  const toggle = (night: RaceNight) => {
    const id = night.occurrenceId;
    setPanelError(null);
    // Guests typed for one night don't carry to another; they DO survive a reload of the same night.
    if (openId !== id) { setGuests([]); setRig(defaultRig(night)); setPayment(null); }
    setOpenId(openId === id ? null : id);
  };

  const claim = async (night: RaceNight) => {
    setBusy(true); setPanelError(null); setNotice('');
    const named = guests.filter((g) => g.firstName.trim());
    // The same resolution the panel shows: the choice while it's offered, else the first available.
    const memberPay = effectivePayment<MemberPayment>(night.options, payment);
    const seats = named.map((g) => ({ ...g, payment: effectivePayment<GuestPayment>(night.guestOptions, g.payment ?? 'card') ?? 'card' }));
    const body: ClaimRaceNightBody = {
      rigClass: rig,
      ...(memberPay ? { payment: memberPay } : {}),
      // Omitted when alone. Stripe only when a guest pays by card: the server refuses any other method,
      // and a claim whose guests all pay with credits has no card to take.
      ...(seats.length ? { guests: seats } : {}),
      ...(seats.some((g) => g.payment === 'card') ? { guestPaymentMethod: PAYMENT_METHOD_STRIPE } : {}),
    };
    let result: ClaimRaceNightResult;
    try {
      result = await raceApi.claimRaceNight(location, night.occurrenceId, body);
    } catch {
      result = { ok: false, status: 0, error: 'Couldn’t reach the venue. Please try again.' };
    }
    setBusy(false);

    // 202: credit seats are spent at hold time, so the balance moves before the card is taken.
    if (result.ok && 'awaitingPayment' in result) { hold.begin(result, location); void refreshBalance(); return; }
    if (result.ok) {
      void refreshBalance();
      setBooked({ reference: result.confirmationCode ?? result.bookingId, hasCode: Boolean(result.confirmationCode) });
      setOpenId(null); setGuests([]); void load();
      return;
    }
    if (result.reason === 'includedLimit') {
      // Spec §6 wording, not the server's. The list reloads so Included comes back greyed.
      setPanelError({ message: includedLimitMessage(night.includedLimit?.max, result.nextFreesOn ?? night.includedLimit?.nextFreesOn) });
      void load();
      return;
    }
    if (FULL_REASONS.includes(result.reason ?? '')) {
      setNotice('That night just filled up. You can join the waitlist.');
      void load();
      return;
    }
    if (result.reason === 'alreadyRegistered') {
      setNotice('You already have a seat on this night.');
      void load();
      return;
    }
    const insufficient = result.reason === 'insufficientCredits';
    // The header may be showing a stale balance that made the seats look affordable.
    if (insufficient) void refreshBalance();
    // paymentNotOffered and the rest (guestIsYou, guestsNotAllowed, minimumCharge…): the server's words.
    setPanelError({ message: result.error, buyCredits: insufficient });
    // What the night offers changed under the member: reload so the pay choices are the server's again.
    if (result.reason === 'paymentNotOffered') void load();
  };

  // Q3: a full night's queue, for the member alone. Staff promote; nothing here takes a seat.
  const joinWaitlist = async (night: RaceNight) => {
    setBusy(true); setPanelError(null); setNotice('');
    const result = await raceApi.joinRaceNightWaitlist(location, night.occurrenceId, rig)
      .catch(() => ({ ok: false as const, status: 0, error: 'Couldn’t reach the venue. Please try again.', reason: undefined }));
    setBusy(false);
    if (result.ok) { void refreshBalance(); void load(); return; }
    if (result.reason === 'seatsOpen') {
      setNotice('A seat just opened on this night. You can claim it now.');
      void load();
      return;
    }
    setPanelError({ message: result.error });
  };

  const leaveWaitlist = async (night: RaceNight) => {
    setBusy(true); setPanelError(null);
    const left = await raceApi.leaveRaceNightWaitlist(location, night.occurrenceId).catch(() => false);
    setBusy(false);
    if (left) { void refreshBalance(); void load(); return; }
    setPanelError({ message: 'Couldn’t leave the waitlist just now. Please try again.' });
  };

  const breadcrumb = <PageBreadcrumb items={[{ label: 'Dashboard', to: '/dashboard' }, { label: 'Race nights' }]} />;

  if (booked) {
    return (
      <>
        {breadcrumb}
        <div className="container py-4">
          <div className="alert alert-success" role="status">
            <h4>You&apos;re in!</h4>
            <p className="mb-0">
              {booked.hasCode ? 'Confirmation code' : 'Booking reference'}: <strong>{booked.reference}</strong>
            </p>
          </div>
          <button type="button" className="btn btn-outline-light" onClick={() => setBooked(null)}>Back to race nights</button>
        </div>
      </>
    );
  }

  // The card went through but the booking is not confirmed: no card affordance at all, only the
  // idempotent re-check (the P0 rule).
  if (hold.chargedButUnconfirmed) {
    return (
      <>
        {breadcrumb}
        <div className="container py-4">
          <div className="booking-create-content">
            <h4 className="text-white">Pay by card</h4>
            <div className="alert alert-warning" role="alert">
              <strong>Your payment went through.</strong> We couldn&apos;t finish confirming the booking.{' '}
              {hold.canRecheck
                ? 'Check the status below, and if it still doesn’t confirm please contact us and we’ll sort it out — do not pay again.'
                : 'Please contact us with the reference below and we’ll sort it out — do not pay again.'}
              {hold.chargedBookingId && <> Reference: <strong>{hold.chargedBookingId}</strong></>}
            </div>
            {hold.canRecheck && (
              <button type="button" className="btn btn-outline-light" onClick={() => void hold.recheck()} disabled={hold.isRechecking}>
                {hold.isRechecking ? 'Checking…' : 'Check booking status'}
              </button>
            )}
          </div>
        </div>
      </>
    );
  }

  if (hold.pending) {
    return (
      <>
        {breadcrumb}
        <div className="container py-4">
          <div className="booking-create-content">
            <h4 className="text-white">Pay by card</h4>
            {/* The card may be for the member's own seat, their guests, or both (the same words as Book a session). */}
            <p className="text-paragraphs">We&apos;re holding your seats while you pay for the seats you chose to pay by card.</p>
            <GuestPaymentForm
              clientSecret={hold.pending.clientSecret}
              bookingId={hold.pending.bookingId}
              intentId={hold.pending.intentId}
              // The venue that HOLDS the booking, never the chip (Review Focus 2).
              location={hold.heldAt}
              amountMinor={hold.pending.amountMinor}
              buyerName={`${user?.firstName ?? ''} ${user?.lastName ?? ''}`.trim()}
              buyerEmail={user?.email ?? ''}
              onPaid={hold.onCardPaid}
              onBack={hold.onBack}
              returnPath="/race-nights"
            />
          </div>
        </div>
      </>
    );
  }

  const isMember = Boolean(membership && membership.name !== 'None');

  return (
    <>
      {breadcrumb}
      <div className="container py-4">
        <div className="booking-create-content rn-page">
          <h1 className="rn-h1">Race nights</h1>
          <p className="rn-sub">
            {isMember
              ? `Nights your ${membership!.name} membership can claim, and what each one costs you.`
              : 'Members race most nights free. You can book any public night here.'}
          </p>

          <div className="wizard-field">
            <span className="wizard-label" id="rn-loc-label">Location</span>
            <div className="wizard-chips" role="group" aria-labelledby="rn-loc-label">
              {VENUES.map((v) => (
                <button key={v.key} type="button" className="wizard-chip" aria-pressed={location === v.key}
                  onClick={() => setLocation(v.key)}>{v.label}</button>
              ))}
            </div>
          </div>

          {notice && <div className="alert alert-info mt-3" role="status">{notice}</div>}
          {loadError && <div className="alert alert-warning mt-3" role="alert">{loadError}</div>}

          {nights === null ? (
            <div className="text-center py-4"><span className="spinner-border" role="status" aria-label="Loading race nights" /></div>
          ) : nights.length === 0 && !loadError ? (
            <p className="rn-empty">No race nights at this venue in the next 60 days.</p>
          ) : (
            GROUPS.map((group) => {
              const inGroup = nights.filter((n) => groupOf(n.date) === group);
              if (inGroup.length === 0) return null;
              return (
                <section key={group} className="rn-group" aria-label={group}>
                  <span className="wizard-label">{group}</span>
                  {inGroup.map((n) => (
                    <div key={n.occurrenceId} className="rn-item">
                      <RaceNightRow night={n} open={openId === n.occurrenceId} onToggle={() => toggle(n)} />
                      {openId === n.occurrenceId && (
                        <RaceNightPanel
                          night={n}
                          memberName={`${user?.firstName ?? ''} ${user?.lastName ?? ''}`.trim()}
                          tierName={isMember ? membership!.name : null}
                          balance={balance}
                          rig={rig}
                          onRigChange={setRig}
                          payment={payment}
                          onPaymentChange={setPayment}
                          guests={guests}
                          onGuestsChange={setGuests}
                          busy={busy}
                          error={panelError}
                          onClaim={() => void claim(n)}
                          onJoinWaitlist={() => void joinWaitlist(n)}
                          onLeaveWaitlist={() => void leaveWaitlist(n)}
                        />
                      )}
                    </div>
                  ))}
                </section>
              );
            })
          )}

          {membership !== undefined && !isMember && (
            <p className="rn-note">
              Members-only nights are not listed.{' '}
              <a href={WEBSITE_MEMBERSHIPS} target="_blank" rel="noreferrer">Compare memberships</a>
            </p>
          )}
        </div>
      </div>
    </>
  );
};

export default RaceNights;
